import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { analyticsClient } from './api';
import {
  AddonExtension,
  EnabledAddon,
  GisArtifact,
  GisMapActionResponse,
  VisualisationArtifact,
  VisualisationChartBuilderRequest,
  VisualisationChartBuilderResponse,
} from '../types/addons';

export const VISUALISATION_ADDON_ID = 'stratforth.visualisation';
export const GIS_ADDON_ID = 'stratforth.gis';
export const ADDON_ID_HEADER = 'X-Stratforth-Addon-ID';
export const CORRELATION_ID_HEADER = 'X-Correlation-ID';

const createCorrelationId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `corr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createAddonHeaders = (
  addonId: string = VISUALISATION_ADDON_ID,
  correlationId: string = createCorrelationId()
): Record<string, string> => ({
  [ADDON_ID_HEADER]: addonId,
  [CORRELATION_ID_HEADER]: correlationId,
});

export const createAddonRequestConfig = (
  addonId: string = VISUALISATION_ADDON_ID,
  config: AxiosRequestConfig = {}
): AxiosRequestConfig => ({
  ...config,
  headers: {
    ...config.headers,
    ...createAddonHeaders(addonId),
  },
});

export const addonApi = {
  listEnabled: async (): Promise<EnabledAddon[]> => {
    const response: AxiosResponse<EnabledAddon[]> = await analyticsClient.get('/api/v1/addons/enabled');
    return response.data;
  },

  listExtensions: async (
    extensionPoint?: string,
    dataObjectId?: string
  ): Promise<AddonExtension[]> => {
    const response: AxiosResponse<AddonExtension[]> = await analyticsClient.get(
      '/api/v1/addons/extensions',
      {
        params: {
          ...(extensionPoint ? { extensionPoint } : {}),
          ...(dataObjectId ? { dataObjectId } : {}),
        },
      }
    );
    return response.data;
  },
};

export const visualisationArtifactApi = {
  list: async (artifactType?: string): Promise<VisualisationArtifact[]> => {
    const response: AxiosResponse<VisualisationArtifact[]> = await analyticsClient.get(
      '/api/v1/addons/visualisation/artifacts',
      createAddonRequestConfig(VISUALISATION_ADDON_ID, {
        params: artifactType ? { artifactType } : undefined,
      })
    );
    return response.data;
  },

  get: async (artifactId: string): Promise<VisualisationArtifact> => {
    const response: AxiosResponse<VisualisationArtifact> = await analyticsClient.get(
      `/api/v1/addons/visualisation/artifacts/${artifactId}`,
      createAddonRequestConfig()
    );
    return response.data;
  },

  buildChart: async (
    payload: VisualisationChartBuilderRequest
  ): Promise<VisualisationChartBuilderResponse> => {
    const response: AxiosResponse<VisualisationChartBuilderResponse> = await analyticsClient.post(
      '/api/v1/addons/visualisation/artifacts/chart-builder',
      payload,
      createAddonRequestConfig()
    );
    return response.data;
  },
};

export const gisArtifactApi = {
  listLayers: async (): Promise<GisArtifact[]> => {
    const response: AxiosResponse<GisArtifact[]> = await analyticsClient.get(
      '/api/v1/addons/gis/artifacts/layers',
      createAddonRequestConfig(GIS_ADDON_ID)
    );
    return response.data;
  },

  readDataObjectMapAction: async (dataObjectId: string): Promise<GisMapActionResponse> => {
    const response: AxiosResponse<GisMapActionResponse> = await analyticsClient.get(
      `/api/v1/addons/gis/data-objects/${dataObjectId}/map-action`,
      createAddonRequestConfig(GIS_ADDON_ID)
    );
    return response.data;
  },
};
